import Doms from "oc-commons/Doms.js";
import Scenes from "oc-threejs/Scenes.js";
import Screens from "oc-threejs/Screens.js";
import {v3, v2, quat} from "oc-threejs/short3.js";
import Maths3 from "oc-threejs/Maths3.js";
import "./events/pantouch.js";
import Dragswitch from "./Dragswitch.js";
import DeviceDetection from "oc-commons/DeviceDetection.js";
import HBS from "./hbs/HBS.js";

/**
 * Der BaseInteractionHandler3D behandelt Tap, Hover und Drag&Drop von 3D-Objekten innerhalb der Szene sowie
 * Rein- und Rausdraggen von 3D-Objekten in/aus der Szene sowie von 2D-Objekten (z.B. Materialeigenschaften)
 * in die Szene.
 * Der BaseInteractionHandler3D überlässt das konkrete Drag/Drop-Verhalten im 3D-Raum abgeleiteten Klassen,
 * also wie genau Objekte im Raum positioniert und orientiert werden.
 *
 * ## Events
 *
 * Die folgenden Events werden auf der Stage ausgelöst, per trigger('eventName'):
 *
 * - tapDraggable für ein Tap/Click auf das Draggable
 * - tapObject3D für ein Tap/Click auf ein dafür empfängliches 3D-Objekt
 * - tapSceneOnly für ein Tap/Click auf die 3D-Scene, ohne das man ein empfängliches Objekt trifft
 * - drag2dBegin
 * - drag2dSceneEnter
 * - drag2d
 * - drop2d
 * - drag2dCancel
 * - drag2dSceneLeave
 * - drag2dEnd
 * - drag3dBegin
 * - drag3dSceneEnter
 * - drag3dSceneEnterImmediate (wird im gegensatz zum dragsceneenter im jeden fall ausgelöst sobald die Maus über der Szene ist])
 * - drag3d
 * - drop3d
 * - drag3dSceneLeave
 * - drag3dCancel
 * - drag3dEnd
 * - dragRootContainerEnter
 * - dragRootContainerLeave
 *
 * ## Callback-Methoden und Properties
 *
 * Siehe **Object3DExtensions.js** für die Properties und Callback-Methoden auf 3D-Objekten.
 *
 * ## Detaching
 *
 * Ein 3D-gedraggtes Objekt bleibt Child seines Parents. Die Positionierung während des Drags wird daher
 * ins lokale Koordinatensystem des Parents übertragen. Falls man das anders haben möchte, muss man das
 * selbst implementieren, z.B. indem man die onDragBegin - Methode des Objektes überschreibt und dort
 *```
 *    THREE.SceneUtils.detach(this, this.parent, STAGE.scene);
 *```
 * aufruft.
 *
 */
export default class BaseInteractionHandler3D {

    stageEventNames = [
        'tapDraggable',
        'tapObject3D',
        'tapSceneOnly',
        'drag2dBegin',
        'drag2dSceneEnter',
        'drag2d',
        'drop2d',
        'drag2dCancel',
        'drag2dSceneLeave',
        'drag2dEnd',
        'drag3dBegin',
        'drag3dSceneEnter',
        'drag3dSceneEnterImmediate',
        'drag3d',
        'drop3d',
        'drag3dSceneLeave',
        'drag3dCancel',
        'drag3dEnd',
        'dragRootContainerEnter',
        'dragRootContainerLeave',
    ];

    event = null;
    // war die Quelle des ersten DragDown - Events die 3D-Scene? Sonst war es ein draggable, also ein anderes DOM-Element wie ein Produktbild
    dragSourceWasScene = false;
    // falls dragSourceWasScene==false ist, wird hier das draggable referenziert, das die Quelle des DragDown-Events war
    sourceDraggable = null;
    // ist das Maus/Touch Event über bzw. innerhalb der 3D-Scene?
    eventIsOverScene = false;
    // das gedraggte Datenobjekt (meist Produkt oder Material), das dem 3D-Object bzw. dem Thumbnail zugeordnet ist
    dragData = null;
    // das gedraggte 2D-Bild/Thubnail (also z.B. ein Clone des Produkt-Divs, auf dem man den Dragvorgang startet, bzw. das vom dragObject3d erzeugte Thumbnail)
    dragDomElement = null;
    // ist das dragDomElement visible? Es wird nur ausserhalb der Scene angezeigt
    dragDomElementVisible = false;
    // das 3D-Objekt, das potentiell gedragged wird (bei interactionType='drag3d').
    dragObject3d = null;
    // speichert Position, Scale, Orientierung und Parent des gedraggten 3D-Objektes, falls es aus der Scene stammt,
    // um im Falle eines DragCancels die ursprünglichen Werte zurückzusetzen
    dragObject3dMemento = null;
    // das 3D-Object, das potentiell ein receiveDropable (bei interactionType='drag2d') empfängt. In diesem Fall würde data übergeben.
    receiveDropableObject3d = null;
    // das zuletzt gehoverte receiveDropableObject3d
    receiveDropableObject3dHovered = null;
    // das 3D-Objekt, auf das potentiell getapped wurde. Die Entscheidung, ob es ein Tap oder ein Drag war, erfolgt oft mit einer Verzögerung.
    tapObject3d = null;
    // das 3D-Object, das gehovered wird
    hoveredObject3d = null;
    // ist schon entschieden, ob es sich um einen Dragvorgang handelt oder um einen Tap oder um keins von beiden?
    // kann die Zustände 'undecided', 'drag3d', 'drag2d', 'tap', oder 'none' enthalten
    interactionType = 'undecided';
    // wurde das onDragSceneEnter - Event bereits gesendet? Dient dazu, onDragSceneEnter auch dann auszulösen, wenn der Dragvorgang nur innerhalb der Scene stattfindet.
    dragSceneEnterTriggered = false;
    // wurde das onDragSceneEnterImmediate - Event bereits gesendet? Dient dazu, onDragSceneEnterImmediate auch dann auszulösen, wenn der Dragvorgang nur innerhalb der Scene stattfindet.
    dragSceneEnterImmediateTriggered = false;
    // true, wenn der Ursprung des Drags ein bestehendes Objekt3D der Szene war, sonst false, wenn es durch den Drag erzeugt wurde (Drag von Thumbnail in die Szene)
    dragObject3dExistedBefore = false;
    // während des Draggens darf sich die Camera nicht bewegen (lock), aber eventuell war die Camera schon vorher gelockt.
    wasCamerlockedByMe = false;
    // die Bildschirmposition, an der das dragdown - Event auftrat
    origin = {
        x: 0,
        y: 0,
        t: 0,
    };
    // ein Timer, der mit entscheidet, ob es ein Tap war. Nach Ablauf einer Mindestzeit ist es kein Tap mehr.
    noTapTimer = null;
    // die Dragswitch-StreamId zum noTapTimer
    noTapTimerStreamId = Number.NaN;

    // true während Camerabewegungen mit Maus / Touch
    orbitcontrolCameraMovement = false;

    // globales Flag, um den InteractionHandler zeitweise auszuschalten. Es ist dann kein Klick/Drag/Drop mehr möglich, alle Events laufen ins Leere.
    locked = false;

    /*
     */
    constructor(rootContainer = $('body')) {
        this.rootContainer = rootContainer;
        this.config = STAGE.settings.dragDrop || {};
        STAGE.on('animatorTick', (now, tags) => this.onAnimatorTick(now,tags));
        this._resetHandle();
        STAGE.on('orbitControlsStart',()=>this.orbitcontrolCameraMovement=true);
        STAGE.on('orbitControlsEnd',()=>this.orbitcontrolCameraMovement=false);
    }

    /**
     * Macht ein DOM-Element draggable. Das zurückgegebene Objekt - der DragContext - stellt Callback-Methoden bereit.
     * @param element
     * @example
     * this.interactionHandler.draggable(element)
     *   // onTap ist optional und kann ein Callback für den Tap/Click Fall auf das Handle selbst setzen
     *   .onTap((handle)=>Dialogs.showToast('Tap on '+product.name+' Draggable'))
     *   // setDragObject3D ist optional und kann ein Callback für das 3D-Modell des gedraggten Objektes setzen.
     *   // hiermit unterscheidet sich, ob es sich um einen 2D oder einen 3D-Drag handelt!
     *   // ein 2D-Drag wie eine Materialeigenschaft hat kein 3D-Modell.
     *   .setDragObject3D((handle)=>product.createObject3d())
     *   // setDragData ist optional und sollte ein Callback für die Produktdaten des gedraggten Objekt zurückgeben
     *   .setDragData((handle)=>product)
     *   // setDragDomElement ist optional, hier kann man ein Callback für das zurückgegebene 2D-Dragelement setzen.
     *   // Standardmäßig ist es ein Clone des Elements
     *   .setDragDomElement((handle)=>element.clone())
     */
    draggable(element) {

        const call = {
            getDragData: (handle) => {},
            getDragDomElement: (handle) => element.clone(),
            getDragObject3D: (handle) => null,
            onTap: (handle) => {},
            isDraggable:()=>true
        };

        const dragContext = {
            setDragData: callback => {
                call.getDragData = callback;
                return dragContext;
            },
            setDragDomElement: callback => {
                call.getDragDomElement = callback;
                return dragContext;
            },
            setDragObject3D: callback => {
                call.getDragObject3D = callback;
                return dragContext;
            },
            onTap: callback => {
                call.onTap = callback;
                return dragContext;
            },
            isDraggable: callback => {
                call.isDraggable = callback;
                return dragContext;
            },
            call
        };

        element = $(element);
        if (element[0]!==undefined && !(element[0] instanceof Element)) {
            debugger;
            throw 'only dom elements can be draggable';
        }
        element.data('customDraggable', dragContext);
        element.addClass('noTouchAction');

        return dragContext;
    }

    /**
     * Macht die STAGE.scene empfänglich für Drop3d, Tap3d, ReveiveDroppable, Hover, ...
     * Muss aufgerufen werden, nachdem der 3d-Canvas zum DOM hinzugefügt wurde
     */
    setSceneDroppable() {

        STAGE.canvas.data('isStageCanvas', true);
        STAGE.canvas.parent().addClass('noTouchAction');

        const logging = false;

        const on = (eventSource,eventName,callback) => {
            eventSource.on(eventName,(event)=>{
                if (logging) {
                    console.log(eventName,event);
                }
                if (!this.locked) {
                    callback(event);
                }
            });
        };

        this.dragswitch = new Dragswitch(this.rootContainer,'InteractionHandler');

        on(this.dragswitch,'dragdown', (event) => {
            // debugger;
            //this.debugLog('dragdown >');
            this._resetHandle();
            this._setEvent(event);
            this._setOriginFromEvent();
            this.dragSourceWasScene = this.eventIsOverScene;
            if (this.dragSourceWasScene) {
                this.fetchObjectsInSceneForDragTap();
                if (this.dragObject3d) {
                    this._lockCamera();
                    this.dragObject3dExistedBefore = true;
                }
                if (this.dragObject3d && this.tapObject3d) {
                    this.interactionType = 'undecided';
                } else if (this.dragObject3d) {
                    this.interactionType = 'drag3d';
                    //this.debugLog('_triggerDrag3dBegin >');
                    this._triggerDrag3dBegin(event);
                    //this.debugLog('_triggerDrag3dBegin <');
                } else {
                    this.interactionType = 'tap';
                }
            } else {
                this.sourceDraggable = this.target.data('customDraggable');
                if (this.sourceDraggable && this.sourceDraggable.call.isDraggable()) {
                    this.interactionType = 'undecided';
                } else {
                    this.interactionType = 'none';
                }
            }
            if (this.interactionType === 'undecided') {
                this._startNoTapTimer();
            }

            if (this.interactionType !== 'none' && DeviceDetection.isIOS()) {
                event.preventDefault();
            }

            if (this.eventIsOverScene) {
                if (this.interactionType === 'drag2d' || this.interactionType === 'drag3d') {
                    //this.debugLog('_maybeTriggerDragSceneEnter >');
                    this._maybeTriggerDragSceneEnter(event);
                    //this.debugLog('_maybeTriggerDragSceneEnter <');
                }
            }
            //this.debugLog('dragdown <');
        });

        on(this.dragswitch,'dragup', (event) => {
            //this.debugLog('dragup >');
            this._setEvent(event);
            this._unlockCamera();
            this._cancelNoTapTimer();

            // debugger;

            if (this.interactionType === 'undecided') {
                if (this._outOfTapTresholds()) {
                    this._itWasADragDecideIf2dOr3d(event);
                } else {
                    this.interactionType = 'tap';
                }
            }

            if (this.interactionType === 'tap') {
                if (!this._outOfTapTresholds()) {
                    if (this.eventIsOverScene) {
                        if (this.tapObject3d) {
                            this._triggerTapObject3D(event);
                        } else {
                            this._triggerTapSceneOnly(event);
                        }
                    } else {
                        if (this.sourceDraggable) {
                            this._triggerTapDraggable(event);
                        }
                    }
                }
            } else if (this.interactionType === 'drag2d') {
                if (this.eventIsOverScene) {
                    this._maybeTriggerDragSceneEnter(event);
                    this._triggerDrag2d(event);
                    if (this.receiveDropableObject3d) {
                        this._triggerDrop2d(event);
                    } else {
                        this._triggerDrag2dCancel(event);
                    }
                } else {
                    this._triggerDrag2dCancel(event);
                }
                this._triggerDrag2dEnd(event);
            } else if (this.interactionType === 'drag3d') {
                if (this.eventIsOverScene) {
                    this._maybeTriggerDragSceneEnter(event);
                    this._triggerDrag3d(event);
                    this._triggerDrop3d(event);
                } else {
                    this._triggerDrag3dCancel(event);
                }
                this._triggerDrag3dEnd(event);
            }


            this._resetHandle();
            //this.debugLog('dragup <');
        });

        on(this.dragswitch,'dragmove', (event)=>{
            //this.debugLog('dragmove >');
            this._setEvent(event);

            if (this.interactionType==='undecided') {
                if (this._outOfTapTresholds()) {
                    this._itWasADragDecideIf2dOr3d(event);
                }
            }

            if (this.interactionType==='drag2d') {
                this._triggerDrag2d(event);
            } else if (this.interactionType==='drag3d') {
                this._maybeTriggerDragSceneEnter(event);
                this._triggerDrag3d(event);
            }
            //this.debugLog('dragmove <');
        });

        if (this.config.cursorChangesOverDraggables) {
            on($("body"),'mousemove', (event) => {
                if (!this.orbitcontrolCameraMovement && this.interactionType !== 'drag2d' && this.interactionType !== 'drag3d') {
                    //this.debugLog('mousemove >');
                    this._setCursor(event);
                    //this.debugLog('mousemove <');
                }
            });
        }

        on(STAGE.canvas,'dragenter', (event) => {
            this._setEvent(event);
            if (this.interactionType==='undecided') {
                this._itWasADragDecideIf2dOr3d(event);
            }
            if (this.interactionType==='drag2d' || this.interactionType==='drag3d') {
                //this.debugLog('dragenter >');
                this._maybeTriggerDragSceneEnter(event);
                //this.debugLog('dragenter <');
            }
        });

        on(STAGE.canvas,'dragleave', (event) => {
            this._setEvent(event);
            if (this.interactionType==='drag2d' || this.interactionType==='drag3d') {
                //this.debugLog('dragleave <');
                this._triggerDragSceneLeave(true);
                //this.debugLog('dragleave <');
            }
        });

        on(this.dragswitch,'dragenter', (event) => {
            this._setEvent(event);
            if (this.interactionType==='drag2d' || this.interactionType==='drag3d') {
                //this.debugLog('dragenter <');
                this._triggerDragRootContainerEnter(event);
                //this.debugLog('dragenter <');
            }
        });

        on(this.dragswitch,'dragleave', (event) => {
            this._setEvent(event);
            if (this.interactionType==='drag2d' || this.interactionType==='drag3d') {
                //this.debugLog('dragleave <');
                this._triggerDragRootContainerLeave(event);
                //this.debugLog('dragleave <');
            }
        });
    }

    //
    // debugLog(s) {
    //     // zum Debuggen nützlich
    //     let position = _.get(this.dragObject3d,'position');
    //     const pc = _.get(this.dragObject3d,'parent');
    //     if (this.PC__!==pc && this.PC__==null !== pc==null) {
    //         console.log(s,pc);
    //     }
    //     if (position && (!this.POSITION__ || this.POSITION__.distanceTo(position||v3())>50)) {
    //         console.log(s,position);
    //     }
    //     this.PC__=pc;
    //     this.POSITION__ = position ? position.clone() : null;
    // }

    /**
     * gibt die 3D-Objekte zurück, die auf Tap/Drag/Hover reagieren.
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    getInteractingObjects3D() {
        return STAGE.scene.children;
    }

    /**
     * Gibt zu einem 3D-Object die dragData zurück (meist userData, oder sowas wie die ArtikelID)
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    getDragDataFromObject3D(dragObject3d) {
        return dragObject3d.userData;
    }

    /**
     * Gibt zu einem dragData ein 2D-DragElement (dragDomElement) zurück.
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    getDragDomElementFromDragData(dragData) {
        return $(HBS('oc-interaction/defaultDragHandle.hbs')(dragData));
    }

    /**
     * Setzt während des Dragvorgangs Position, Orientierung, Scale usw. des DragObject3d.
     * Zum Überschreiben in abgeleiteteten Klassen!
     * @param dropped true wenn es ein Drop-Event ist
     */
    setDragObject3dPosition(dropped) {
        // Beispiel-Implementierung
        if (this.eventIsOverScene) {
            const point = Screens.rayCastAtMouseOnPlane(this.event,Maths3.plane.y0);
            if (point && this.dragObject3d.parent) {
                const bounds = Scenes.boundingBox(this.dragObject3d);
                point.setY(bounds.getSize().y/2);
                this.dragObject3d.parent.worldToLocal(point);
                this.dragObject3d.position.copy(point);
                STAGE.dirty();
            }
        }
    }

    /**
     * führt eventuell ein update durch - wird von oc-virtual3D/AbstractRepresenter aufgerufen,
     * falls es eventuell eine Änderung der Restraints gibt.
     */
    maybeUpdate() {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    isDraggable(object) {
        return object.draggable;
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    isTapable(object) {
        return object.tapable;
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onTapDraggable() {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     * return false => Object3D.onTap wird nicht aufgerufen
     * @returns {boolean}
     */
    onTapObject3D(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onTapSceneOnly(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag2dBegin(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag2d(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrop2d(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag2dCancel(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag2dEnd(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3dBegin(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3d(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrop3d(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3dCancel(event) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3dEnd(event) {
    }

    /**
     * SceneEnter wird als Teil des normalen Lifecycles bei jedem
     * Drag-Vorgang geworfen.
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3dSceneEnter(event) {
    }

    /**
     * SceneEnterImmediate wird als Teil des normalen Lifecycles bei jedem
     * Drag-Vorgang geworfen. Im Gegensatz zu onDrag3dSceneEnter wird
     * diese Event in jedem Fall immer sofort geworfen sobald die Maus die
     * Szene betritt.
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag3dSceneEnterImmediate(event) {
    }

    /**
     * SceneEnter wird als Teil des normalen Lifecycles bei jedem
     * Drag-Vorgang geworfen.
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDrag2dSceneEnter(event) {
    }

    /**
     * Siehe SceneEnter
     * Zum Überschreiben in abgeleiteteten Klassen.
     * @param real true if the mouse goes to the outside
     */
    onDrag3dSceneLeave(real) {
    }

    /**
     * Siehe SceneEnter
     * Zum Überschreiben in abgeleiteteten Klassen.
     * @param real true if the mouse goes to the outside
     */
    onDrag2dSceneLeave(real) {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDragRootContainerEnter() {
    }

    /**
     * Zum Überschreiben in abgeleiteteten Klassen.
     */
    onDragRootContainerLeave() {
    }

    /**
     * wird bei jedem Animator-Tick aufgerufen
     * @param {number} now
     * @param {Set} tags
     */
    onAnimatorTick(now,tags) {
    }

    onTouch(){
    }

    /**
     * Wird bei einem DragDown in der Szene aufgerufen und sucht nach Objekten auf dem
     * Mausray, die in Frage kommen für tap oder drag.
     */
    fetchObjectsInSceneForDragTap() {
        this.tapObject3d = this.getTapObject3dFromScene();
        this.dragObject3d = this.getDragObject3dFromScene();
        if (this.dragObject3d) {
            this.dragData = this.getDragDataFromObject3D(this.dragObject3d);
            this._hideDragDomElement();
            this.dragDomElement = this.getDragDomElementFromDragData(this.dragData);
        }
    }

    /** */
    fetchObjectsInSceneForDrag2d() {
        this.receiveDropableObject3d = this.getReceiveDropableObject3dFromScene();
    }


    /**
     * Gibt das eventuell unter dem Mausray liegende Tap-Object zurück
     */
    getTapObject3dFromScene() {
        const interactingObjects = this.getInteractingObjects3D();
        return Scenes.findUp(
            STAGE.changeRaycastVisibilityDuring('tap',
                ()=>Screens.fetchObjectAtMouse(this.event, interactingObjects)
            ),
            (o)=>this.isTapable(o)
        );
    }

    /**
     * Gibt das eventuell unter dem Mausray liegende Drag3d-Object zurück
     */
    getDragObject3dFromScene() {
        const interactingObjects = this.getInteractingObjects3D();
        return Scenes.findUp(
            STAGE.changeRaycastVisibilityDuring('drag',
                ()=>Screens.fetchObjectAtMouse(this.event, interactingObjects)
            ),
            (o)=>this.isDraggable(o)
        );
    }

    /**
     * Gibt das eventuell unter dem Mausray liegende ReceiveDropable-Object zurück
     * (welches das aktuell gedraggte 2d-Objekt aufnehmen kann)
     */
    getReceiveDropableObject3dFromScene() {
        const interactingObjects = this.getInteractingObjects3D();
        return Scenes.findUp(
            STAGE.changeRaycastVisibilityDuring('dragReceiveDropable',
                ()=>Screens.fetchObjectAtMouse(this.event, interactingObjects)
            ),
            (o)=>o.receiveDropable && o.acceptReceiveDrop(this.dragData)
        );
    }

    /**
     * Wird beim DragDown auf ein Draggable (also ein 2D-Element) aufgerufen und initialisiert
     * dragData, dragDomElement und dragObject3d
     */
    createDragObjectsFromDraggable() {
        this.dragData = this.sourceDraggable.call.getDragData(this);
        this._hideDragDomElement();
        this.dragDomElement = Doms.div('.draghandle',this.sourceDraggable.call.getDragDomElement(this));
        this.dragObject3d = this.sourceDraggable.call.getDragObject3D(this);
        if (!(this.interactionType!='drag2d' && this.eventIsOverScene)) {
            this._showDragDomElement();
        }
    }

    /**
     * @private
     */
    _resetHandle() {
        this._hideDragDomElement();
        this._cancelNoTapTimer();
        this.event = null;
        this.dragSourceWasScene = false;
        this.sourceDraggable = null;
        this.eventIsOverScene = false;
        this.dragDomElement = null;
        this.dragDomElementVisible = false;
        this.dragData = null;
        this.dragObject3d = null;
        this.dragObject3dMemento = null;
        this.receiveDropableObject3d = null;
        this.receiveDropableObject3dHovered = null;
        this.tapObject3d = null;
        this.interactionType = 'undecided';
        this.dragSceneEnterTriggered = false;
        this.dragSceneEnterImmediateTriggered = false;
        this.dragObject3dExistedBefore = false;
        this.wasCamerlockedByMe = false;
        this.origin = {
            x: 0,
            y: 0,
            t: 0,
        };
        this.noTapTimer = null;
        this.noTapTimerStreamId = Number.NaN;

        // das 3D-Object, das gehovered wird, wird nicht resetted!
        // this.hoveredObject3d = null;
    }

    /**
     * @private
     */
    _itWasADragDecideIf2dOr3d(event) {
        if (this.dragSourceWasScene) {
            this.interactionType = 'drag3d';
            this._triggerDrag3dBegin(event);
        } else if (this.sourceDraggable) {
            this.createDragObjectsFromDraggable();
            if (this.dragObject3d) {
                this.interactionType = 'drag3d';
                this._triggerDrag3dBegin(event);
            } else {
                this.interactionType = 'drag2d';
                this._triggerDrag2dBegin(event);
            }
        }
    }

    /**
     * @private
     */
    _setOriginFromEvent() {
        this.origin.x = this._mx();
        this.origin.y = this._my();
        this.origin.t = this.event.timeStamp;
    }

    /**
     * @private
     */
    _maybeTriggerDragSceneEnter(event) {
        if (this.eventIsOverScene) {
            if (!this.dragSceneEnterImmediateTriggered) {
                this._triggerDragSceneEnterImmediate(event);
            }
            if (!this.dragSceneEnterTriggered) {
                this._triggerDragSceneEnter(event);
            }
        }

    }

    /**
     * @private
     */
    _triggerTapDraggable(event) {
        this.onTapDraggable(event);
        this.sourceDraggable.call.onTap();
        STAGE.trigger('tapDraggable',this);
    }

    /**
     * @private
     */
    _triggerTapObject3D(event) {
        const proceedWithTab = this.onTapObject3D(event);
        if (proceedWithTab===false) {
            return;
        }
        this.tapObject3d.onTap();
        STAGE.trigger('tapObject3D',this);
    }

    /**
     * @private
     */
    _triggerTapSceneOnly(event) {
        this.onTapSceneOnly(event);
        STAGE.trigger('tapSceneOnly',this);
    }

    /**
     * @private
     */
    _triggerDrag2dBegin(event) {
        this.dragswitch.claimDragStream();
        this._lockCamera();
        this._showCursorDragging(true);
        this.onDrag2dBegin(event);
        STAGE.trigger('drag2dBegin',this);
    }

    /**
     * @private
     */
    _triggerDrag2d(event) {
        this.fetchObjectsInSceneForDrag2d();
        this._setDragDomElementToPosition();
        if (this.receiveDropableObject3d!==this.receiveDropableObject3dHovered) {
            if (this.receiveDropableObject3dHovered) {
                this.receiveDropableObject3dHovered.hovering = false;
                this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
            }
            this.receiveDropableObject3dHovered = this.receiveDropableObject3d;
            if (this.receiveDropableObject3d) {
                this.receiveDropableObject3d.hovering = this.dragData || true;
                this.receiveDropableObject3d.onChangeInDragSelectHoverState();
            }
            STAGE.dirty();
        }
        this.onDrag2d(event);
        STAGE.trigger('drag2d',this);
    }

    /**
     * @private
     */
    _triggerDrop2d(event) {
        this._hideDragDomElement();
        this.onDrop2d(event);
        this.receiveDropableObject3d.onReceiveDrop(this.dragData);
        STAGE.trigger('drop2d',this);
    }

    /**
     * @private
     */
    _triggerDrag2dCancel(event) {
        this._hideDragDomElement();
        this.onDrag2dCancel(event);
        STAGE.trigger('drag2dCancel',this);
    }

    /**
     * @private
     */
    _triggerDrag2dEnd(event) {
        this._showCursorDragging(false);
        if (this.receiveDropableObject3dHovered) {
            this.receiveDropableObject3dHovered.hovering = false;
            this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
        }
        this.onDrag2dEnd(event);
        STAGE.trigger('drag2dEnd',this);
    }

    /**
     * @private
     */
    _triggerDrag3dBegin() {
        this.dragswitch.claimDragStream();
        this._lockCamera();
        this._showCursorDragging(true);
        if (this.dragObject3dExistedBefore) {
            this.dragObject3dMemento = {
                raycastVisibleTags: this.dragObject3d.raycastVisibleTags,
                position: this.dragObject3d.position.clone(),
                quaternion: this.dragObject3d.quaternion.clone(),
                scale: this.dragObject3d.scale.clone(),
                parent: this.dragObject3d.parent,
            };
        } else {
            this.dragObject3dMemento = {
                raycastVisibleTags: this.dragObject3d.raycastVisibleTags,
            };
        }
        this.dragObject3d.dragging = true;
        this.dragObject3d.onChangeInDragSelectHoverState();
        this.dragObject3d.raycastVisibleTags = [];
        this.onDrag3dBegin(event);
        this.dragObject3d.onDragBegin();
        this.dragObject3d.trigger('dragBegin');
        STAGE.trigger('drag3dBegin',this);
        STAGE.animator.start('drag');
        STAGE.dirty();
    }

    /**
     * @private
     */
    _triggerDrag3d(event) {
        this._setDragDomElementToPosition();
        this.setDragObject3dPosition(false);
        this.onDrag3d(event);
        this.dragObject3d.onDrag(event);
        this.dragObject3d.trigger('drag');
        STAGE.trigger('drag3d',this);
        STAGE.dirty();
    }

    /**
     * @private
     */
    _triggerDrop3d(event) {
        this.setDragObject3dPosition(true);
        this.onDrop3d(event);
        this.dragObject3d.raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
        this.dragObject3d.dragging = false;
        this.dragObject3d.onChangeInDragSelectHoverState();
        this.dragObject3d.onDrop();
        this.dragObject3d.trigger('drop');
        STAGE.trigger('drop3d',this);
        STAGE.dirty();
    }

    /**
     * @private
     */
    _triggerDrag3dCancel(event) {
        if (this.dragObject3dExistedBefore) {
            this.dragObject3d.raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
            this.dragObject3d.position.copy(this.dragObject3dMemento.position);
            this.dragObject3d.quaternion.copy(this.dragObject3dMemento.quaternion);
            this.dragObject3d.scale.copy(this.dragObject3dMemento.scale);
            this.dragObject3dMemento.parent.add(this.dragObject3d);
        } else {
            Scenes.remove(this.dragObject3d);
        }
        this.onDrag3dCancel(event);
        if (this.dragObject3dExistedBefore) {
            this.dragObject3d.raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
        }
        this.dragObject3d.dragging = false;
        this.dragObject3d.onDragCancel();
        this.dragObject3d.trigger('dragCancel');
        STAGE.trigger('drag3dCancel',this);
        STAGE.dirty();
    }

    /**
     * @private
     */
    _triggerDrag3dEnd(event) {
        STAGE.animator.stop('drag');
        this._hideDragDomElement();
        this._showCursorDragging(false);
        this.onDrag3dSceneLeave(false);
        this.dragObject3d.onDragLeave(false);
        STAGE.trigger('drag3dSceneLeave',this,false);
        this.onDrag3dEnd(event);
        this.dragObject3d.onDragEnd();
        this.dragObject3d.trigger('dragEnd');
        STAGE.trigger('drag3dEnd',this);
    }

    /**
     * @private
     */
    _triggerDragSceneEnter(event) {
        if (this.dragObject3d) {
            this._hideDragDomElement();
            if (Scenes.isOrphan(this.dragObject3d)) {
                if (this.dragObject3dExistedBefore && !Scenes.isOrphan(this.dragObject3dMemento.parent)) {
                    this.dragObject3dMemento.parent.add(this.dragObject3d);
                } else {
                    STAGE.scene.add(this.dragObject3d);
                }
            }
            this.setDragObject3dPosition(false);
            this.onDrag3dSceneEnter(event);
            this.dragObject3d.onDragEnter();
            STAGE.trigger('drag3dSceneEnter',this);
            STAGE.dirty();
        } else {
            this.onDrag2dSceneEnter(event);
            STAGE.trigger('drag2dSceneEnter',this);
        }
        this.dragSceneEnterTriggered = true;
    }

    /**
     * @private
     */
    _triggerDragSceneEnterImmediate(event) {
        this.dragSceneEnterImmediateTriggered = true;
        this.onDrag3dSceneEnterImmediate(event);
        STAGE.trigger('drag3dSceneEnterImmediate',this);
    }

    /**
     * @private
     */
    _triggerDragSceneLeave(real) {
        if (this.dragObject3d) {
            if (real && this.dragObject3dExistedBefore) {
                this.dragObject3dMemento.parent = this.dragObject3d.parent;
            }
            this._showDragDomElement();
            Scenes.remove(this.dragObject3d);
            this.onDrag3dSceneLeave(real);
            this.dragObject3d.onDragLeave(real);
            STAGE.trigger('drag3dSceneLeave',this,real);
            STAGE.dirty();
        } else {
            this.onDrag2dSceneLeave(real);
            STAGE.trigger('drag2dSceneLeave',this,real);
        }
        if (this.receiveDropableObject3dHovered) {
            this.receiveDropableObject3dHovered.hovering = false;
            this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
            this.receiveDropableObject3dHovered = null;
        }
        this.dragSceneEnterImmediateTriggered = false;
        this.dragSceneEnterTriggered = false;
    }

    /**
     * @private
     */
    _triggerDragRootContainerEnter(event) {
        if (!this.dragObject3d || !this.eventIsOverScene) {
            this._showDragDomElement();
        }
        this.onDragRootContainerEnter(event);
        STAGE.trigger('dragRootContainerEnter',this);
    }

    /**
     * @private
     */
    _triggerDragRootContainerLeave(event) {
        this._hideDragDomElement();
        this.onDragRootContainerLeave(event);
        STAGE.trigger('dragRootContainerLeave',this);
    }

    /**
     * @private
     */
    _outOfTapTresholds() {
        return this._distancePx() > this.config.clickPxTreshold
            || (this._distanceMs() > this.config.clickMsTreshold && this._distancePx() < 2);
    }

    /**
     * @private
     */
    _setEvent(event) {
        this.event = event;
        this.target = $(this.event.target);
        this.eventIsOverScene = this.target.data('isStageCanvas') == true;
    }

    /**
     * @private
     */
    _showDragDomElement() {
        this.dragDomElementVisible = true;
        if (this.dragDomElement) {
            $('*', this.dragDomElement).css({
                'pointer-events': 'none',
            });
            this.dragDomElement.css({
                'position': 'absolute',
                'pointer-events': 'none',
                'z-index': 1000,
                'opacity': 0.8,
            });

            this._setDragDomElementToPosition();
            $('body').append(this.dragDomElement);

            let originalSize = this.dragDomElement.data('originalSize');
            if (!originalSize) {
                this.dragDomElement.data('originalSize',{width: this.dragDomElement.width(), height: this.dragDomElement.height()});
                this._setDragDomElementToPosition();
            }
        }
    }

    /**
     * @private
     */
    _hideDragDomElement() {
        if (this.dragDomElement) {
            this.dragDomElementVisible = false;
            this.dragDomElement.css('opacity',0);
            this.dragDomElement.remove();
        }
    }

    /**
     * @private
     */
    _lockCamera() {
        STAGE.cameralock.setLocked(true);
        this.wasCamerlockedByMe = true;
    }

    /**
     * @private
     */
    _unlockCamera() {
        if (this.wasCamerlockedByMe) {
            STAGE.cameralock.setLocked(false);
            this.wasCamerlockedByMe = false;
        }
    }

    /**
     * @private
     */
    _mx() {
        return this.event.pageX;
    }

    /**
     * @private
     */
    _my() {
        return this.event.pageY;
    }

    /**
     * @private
     */
    _distancePx() {
        const dx = this._mx() - this.origin.x;
        const dy = this._my() - this.origin.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * @private
     */
    _distanceMs() {
        return this.event.timeStamp - this.origin.t;
    }

    /**
     * @private
     */
    _setDragDomElementToPosition() {
        if (this.dragDomElement) {
            let originalSize = this.dragDomElement.data('originalSize');
            const w = originalSize ? originalSize.width/2 : 0;
            const h = originalSize ? originalSize.height/2 : 0;
            this.dragDomElement.css({
                left: this._mx() - w,
                top: this._my() - h
            });
        }
    }

    /**
     * @private
     */
    _startNoTapTimer() {
        this._cancelNoTapTimer();
        this.noTapTimerStreamId = this.dragswitch.getStreamId();
        this.noTapTimer = window.setTimeout(()=> {
            if (this.dragswitch.active() && this.dragswitch.getStreamId() == this.noTapTimerStreamId) {
                this.event.timeStamp = Date.now();
                //this.onMouseMove(this.event);
            }
        }, this.config.clickMsTreshold + 1);
    }

    /**
     * @private
     */
    _cancelNoTapTimer() {
        if (this.noTapTimer) {
            window.clearTimeout(this.noTapTimer);
            this.noTapTimer = null;
            this.noTapTimerStreamId = Number.NaN;
        }
    }

    /**
     * @private
     */
    _showCursorDragging(on) {
        const dragclass = 'cursorDragging';
        if (on) {
            this.rootContainer.addClass(dragclass);
        } else {
            this.rootContainer.removeClass(dragclass);
        }
    }

    /**
     * @private
     */
    _setCursor(event) {
        const canvas = STAGE.canvas;
        let hover = STAGE.changeRaycastVisibilityDuring('hover', ()=>
            Screens.fetchObjectAtMouse(event, STAGE.scene.children));
        hover = Scenes.findUp(hover, object => object.hoverable)
        if (hover) {
            if (this.hoveredObject3d === hover) {
                return;
            }
            if (this.hoveredObject3d) {
                this.hoveredObject3d.hovering = false;
                this.hoveredObject3d.onChangeInDragSelectHoverState();
            }
            this.hoveredObject3d = hover;
            canvas.css('cursor', 'pointer');
            this.hoveredObject3d.hovering = true;
            this.hoveredObject3d.onChangeInDragSelectHoverState();
        } else {
            canvas.css('cursor', 'default');
            if (this.hoveredObject3d) {
                this.hoveredObject3d.hovering = false;
                this.hoveredObject3d.onChangeInDragSelectHoverState();
                this.hoveredObject3d = null;
            }
        }
    }

}
